import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowRight, CheckCircle, Clock, Terminal } from 'lucide-react'
import { documentsApi, jobsApi, type Document, type JobLog, type ProcessingJob } from '../api'
import SubscriptionNotice from '../components/SubscriptionNotice'
import { useToastStore } from '../store/toast'

export default function Processing() {
  const { documentId } = useParams<{ documentId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const addToast = useToastStore((state) => state.addToast)
  const [document, setDocument] = useState<Document | null>(null)
  const [job, setJob] = useState<ProcessingJob | null>(null)
  const [logs, setLogs] = useState<JobLog[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!documentId) return
    documentsApi.get(documentId)
      .then((res) => {
        setDocument(res.data)
        const stateJobId = (location.state as any)?.jobId
        const latestJob = res.data.latest_job
        if (stateJobId) {
          return jobsApi.get(stateJobId).then((jobRes) => setJob(jobRes.data))
        }
        if (latestJob) setJob(latestJob)
      })
      .catch(() => addToast({ type: 'error', message: 'Could not load document.' }))
      .finally(() => setLoading(false))
  }, [documentId, location.state, addToast])

  const jobId = job?.id
  useEffect(() => {
    if (!jobId) return
    const source = new EventSource(jobsApi.eventsUrl(jobId), { withCredentials: true })
    source.addEventListener('log', (event) => {
      const log = JSON.parse((event as MessageEvent).data) as JobLog
      setLogs((items) => items.some((item) => item.id === log.id) ? items : [...items, log])
    })
    source.addEventListener('status', (event) => {
      setJob(JSON.parse((event as MessageEvent).data))
    })
    source.addEventListener('done', () => {
      source.close()
      addToast({ type: 'success', message: 'Processing finished.' })
    })
    source.onerror = () => source.close()
    return () => source.close()
  }, [jobId, addToast])

  const elapsed = useMemo(() => {
    if (!job?.started_at) return '-'
    const end = job.completed_at ? new Date(job.completed_at).getTime() : Date.now()
    return `${Math.max(0, Math.round((end - new Date(job.started_at).getTime()) / 1000))}s`
  }, [job])

  if (loading) return <div className="max-w-6xl mx-auto text-gray-500">Loading...</div>
  if (!document) return null

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-950">Processing</h1>
          <p className="text-gray-600 mt-1">{document.original_filename}</p>
        </div>
        <div className="flex gap-3">
          <Link to="/dashboard" className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Back to dashboard</Link>
          {['completed'].includes(job?.status || '') && (
            <button onClick={() => navigate(`/documents/${document.id}/preview`)} className="px-4 py-2 rounded-lg bg-brand-blue text-white hover:bg-brand-accent">
              Preview data
            </button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <Metric label="Status" value={job?.status || document.status} icon={Clock} />
        <Metric label="Progress" value={`${job?.progress || 0}%`} icon={ArrowRight} />
        <Metric label="Pages" value={job?.pages_processed || document.page_count || 0} icon={CheckCircle} />
        <Metric label="Elapsed" value={elapsed} icon={Clock} />
      </div>

      <SubscriptionNotice context="processing" />

      <section className="bg-gray-950 rounded-lg border border-gray-800 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-800 flex items-center gap-2 text-gray-200">
          <Terminal className="w-5 h-5" />
          <span className="font-medium">Live terminal</span>
        </div>
        <div className="h-[480px] overflow-y-auto p-5 font-mono text-sm text-green-100 space-y-2">
          {logs.length === 0 && <p className="text-gray-500">Waiting for logs...</p>}
          {logs.map((log) => (
            <p key={log.id} className={log.level === 'error' ? 'text-red-300' : 'text-green-100'}>
              <span className="text-gray-500">[{new Date(log.created_at).toLocaleTimeString()}]</span> {log.message}
            </p>
          ))}
        </div>
      </section>
    </div>
  )
}

function Metric({ label, value, icon: Icon }: { label: string; value: any; icon: any }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{label}</p>
        <Icon className="w-5 h-5 text-brand-blue" />
      </div>
      <p className="text-2xl font-bold text-gray-950 mt-2">{value}</p>
    </div>
  )
}
